import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AppShell from "@/layouts/AppShell";
import Dashboard from "@/pages/Dashboard";
import Placeholder from "@/pages/Placeholder";
import Cases from "@/pages/Cases";
import MMBuilder from "@/pages/MMBuilder";
import { MM_BUILDER_ENABLED } from "@/constants/featureFlags";
import { AppearanceProvider } from "@/context/AppearanceContext";
import { WorkspaceModeProvider } from "@/context/WorkspaceModeContext";
import { Ni2DemoStoreProvider } from "@/context/Ni2DemoStore";
import { MMBuilderStoreProvider } from "@/context/MMBuilderStore";
import { LiveModeProvider } from "@/context/LiveModeContext";
import { Ni2ScopeProvider } from "@/context/Ni2ScopeContext";
import { ProvenanceWorkspaceProvider } from "@/context/ProvenanceWorkspaceContext";
import {
  LayoutDashboard,
  FolderKanban,
  History,
  MessageSquareText,
  ClipboardList,
  BookText,
  Library,
  BarChart3,
  Settings,
} from "lucide-react";

function App() {
  return (
    <AppearanceProvider>
      <WorkspaceModeProvider>
        <LiveModeProvider>
        <Ni2ScopeProvider>
        <ProvenanceWorkspaceProvider>
        <Ni2DemoStoreProvider>
        <MMBuilderStoreProvider>
        <BrowserRouter>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/cases"
            element={<Cases />}
          />
          <Route
            path="/timeline"
            element={
              <Placeholder
                title="Surgical Memory Timeline"
                subtitle="Continuous case-by-case memory"
                Icon={History}
                blurb="A time-stitched narrative of every intra-operative event, reflection, imaging capture and voice note — linked back to the case, surgeon and moment it originated from."
              />
            }
          />
          <Route
            path="/ask"
            element={
              <Placeholder
                title="Ask Ni²"
                subtitle="Conversational retrieval"
                Icon={MessageSquareText}
                blurb="A structured conversation surface over the surgeon's own memory — every answer will cite the originating case, reflection, M&M and source literature."
              />
            }
          />
          <Route
            path="/pre-case"
            element={
              <Placeholder
                title="Pre-Case Review"
                subtitle="Focused briefing before OR"
                Icon={ClipboardList}
                blurb="Automatic synthesis of similar prior cases, anatomical caveats, prior reflections and outstanding literature — assembled the night before each scheduled case."
              />
            }
          />
          <Route
            path="/mm"
            element={
              MM_BUILDER_ENABLED ? (
                <MMBuilder />
              ) : (
                <Placeholder
                  title="M&M Builder"
                  subtitle="Morbidity & mortality construction"
                  Icon={BookText}
                  blurb="Compose an M&M presentation directly from captured memory: complications, decision points, reflections, literature comparisons — all fully traceable."
                />
              )
            }
          />
          <Route
            path="/library"
            element={
              <Placeholder
                title="Knowledge Library"
                subtitle="Personal + institutional reference"
                Icon={Library}
                blurb="A curated library of textbook chapters, landmark papers, personal notes and departmental guidance, cross-linked to the cases where each was used."
              />
            }
          />
          <Route
            path="/analytics"
            element={
              <Placeholder
                title="Analytics"
                subtitle="Practice-level pattern review"
                Icon={BarChart3}
                blurb="Volume, complexity mix, complication trends and reflection cadence across a rolling window — with drill-down back into the underlying cases."
              />
            }
          />
          <Route
            path="/settings"
            element={
              <Placeholder
                title="Settings"
                subtitle="Capture, provenance & sync"
                Icon={Settings}
                blurb="Voice-capture preferences, provenance policies, storage, sharing rules and connected devices. Kept quiet, kept out of the way."
              />
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </MMBuilderStoreProvider>
    </Ni2DemoStoreProvider>
    </ProvenanceWorkspaceProvider>
    </Ni2ScopeProvider>
    </LiveModeProvider>
    </WorkspaceModeProvider>
    </AppearanceProvider>
  );
}

// keep LayoutDashboard/FolderKanban imports in tree to avoid tree-shake warnings when reused
void LayoutDashboard;
void FolderKanban;

export default App;
